import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
    saveCartSession, 
    loadCartSession, 
    clearCartSession, 
    generateSessionToken 
} from '../services/cartPersistenceApi';
import { 
    loginClient, 
    logoutClient, 
    checkAuthStatus 
} from '../services/localDbApi';

const ClientPortalContext = createContext();

export const ClientPortalProvider = ({ children }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState(null);
    const [cart, setCart] = useState([]);
    const [orderStarted, setOrderStarted] = useState(false);
    const [sessionToken, setSessionToken] = useState(null);

    // Initialiser ou récupérer le token de session
    useEffect(() => {
        let token = localStorage.getItem('cartSessionToken');
        if (!token) {
            token = generateSessionToken();
            localStorage.setItem('cartSessionToken', token);
        }
        setSessionToken(token);
    }, []);

    // Charger les données utilisateur et panier au démarrage
    useEffect(() => {
        const loadUserData = async () => {
            // Vérifier l'authentification via cookie
            try {
                const authStatus = await checkAuthStatus();
                if (authStatus.authenticated) {
                    setUser(authStatus.user);
                    setIsLoggedIn(true);

                    // Charger le panier depuis PostgreSQL si connecté et token disponible
                    if (sessionToken && authStatus.user.email) {
                        try {
                            const cartResult = await loadCartSession(authStatus.user.email, sessionToken);
                            if (cartResult.success && cartResult.cart_session) {
                                setCart(cartResult.cart_session.cart_data || []);
                                setOrderStarted(cartResult.cart_session.cart_data.length > 0);
                            }
                        } catch (error) {
                            console.warn('⚠️ Impossible de charger le panier depuis la base:', error.message);
                        }
                    }
                }
            } catch (error) {
                console.warn('⚠️ Erreur lors de la vérification de l\'authentification:', error.message);
                setIsLoggedIn(false);
                setUser(null);
            }
        };

        if (sessionToken) {
            loadUserData();
        }
    }, [sessionToken]);

    // Sauvegarder automatiquement le panier lors des modifications
    useEffect(() => {
        const saveCartToDatabase = async () => {
            if (isLoggedIn && user?.email && sessionToken && cart.length > 0) {
                try {
                    await saveCartSession({
                        clientEmail: user.email,
                        sessionToken: sessionToken,
                        cartData: cart
                    });
                } catch (error) {
                    console.warn('⚠️ Impossible de sauvegarder le panier:', error.message);
                    // Continue sans bloquer l'UI
                }
            }
        };

        // Debounce pour éviter trop de sauvegardes
        const timeoutId = setTimeout(saveCartToDatabase, 1000);
        return () => clearTimeout(timeoutId);
    }, [cart, isLoggedIn, user, sessionToken]);

    // login et logout sont maintenant définis directement dans le provider value

    const startOrder = () => {
        setOrderStarted(true);
        setCart([]);
    };

    const cancelOrder = async () => {
        setOrderStarted(false);
        setCart([]);
        
        // Supprimer aussi de la base de données si connecté
        if (isLoggedIn && user?.email && sessionToken) {
            try {
                await clearCartSession(user.email, sessionToken, 'order_cancelled');
            } catch (error) {
                console.warn('⚠️ Impossible de supprimer le panier de la base:', error.message);
            }
        }
    };

    const clearCartAfterSuccess = async (reason = 'quotation_sent') => {
        // Supprimer le panier de la base de données après envoi réussi
        if (isLoggedIn && user?.email && sessionToken) {
            try {
                await clearCartSession(user.email, sessionToken, reason);} catch (error) {
                console.warn('⚠️ Impossible de supprimer le panier de la base:', error.message);
            }
        }
        
        // Vider le panier local
        setCart([]);
        setOrderStarted(false);
    };

    const addToCart = (product, quantity) => {
        // Standardiser l'ID pour éviter les doublons
        const productId = product.item_id || product.id;
        
        setCart(prev => {
            const existingItem = prev.find(item => {
                const itemProductId = item.product.item_id || item.product.id;
                return itemProductId === productId;
            });
            
            if (existingItem) {
                return prev.map(item => {
                    const itemProductId = item.product.item_id || item.product.id;
                    return itemProductId === productId
                        ? { ...item, quantity: quantity }
                        : item;
                });
            } else {
                return [...prev, { product, quantity }];
            }
        });
    };

    const removeFromCart = (productId) => {
        setCart(prev => prev.filter(item => {
            const itemProductId = item.product.item_id || item.product.id;
            return itemProductId !== productId;
        }));
    };

    const updateQuantity = (productId, quantity) => {
        if (quantity <= 0) {
            removeFromCart(productId);
        } else {
            setCart(prev => prev.map(item => {
                const itemProductId = item.product.item_id || item.product.id;
                return itemProductId === productId
                    ? { ...item, quantity }
                    : item;
            }));
        }
    };

    const [token, setToken] = useState(null);

    const getTotalItems = () => {
        return cart.reduce((total, item) => total + item.quantity, 0);
    };

    return (
        <ClientPortalContext.Provider value={{
            isLoggedIn,
            user,
            cart,
            orderStarted,
            login: async (credentials) => {
                try {
                    const userData = await loginClient(credentials);
                    setUser(userData);
                    setIsLoggedIn(true);
                    // Plus besoin de localStorage, l'auth est gérée par cookie
                    return userData;
                } catch (error) {
                    throw error;
                }
            },
            logout: async () => {
                try {
                    // Supprimer le panier de la base avant logout
                    if (isLoggedIn && user?.email && sessionToken) {
                        try {
                            await clearCartSession(user.email, sessionToken, 'user_logout');
                        } catch (error) {
                            console.warn('⚠️ Impossible de supprimer le panier lors du logout:', error.message);
                        }
                    }
                    
                    // Appeler l'API de déconnexion pour supprimer le cookie
                    await logoutClient();
                    
                    setUser(null);
                    setIsLoggedIn(false);
                    setCart([]);
                    setOrderStarted(false);
                    setToken(null);
                    
                    // Garder uniquement le token de session panier
                    // localStorage.removeItem('cartSessionToken'); // On garde ça pour le panier
                } catch (error) {
                    console.error('Erreur lors de la déconnexion:', error);
                    // Forcer le logout local même en cas d'erreur serveur
                    setUser(null);
                    setIsLoggedIn(false);
                    setCart([]);
                    setOrderStarted(false);
                    setToken(null);
                }
            },
            startOrder,
            cancelOrder,
            addToCart,
            removeFromCart,
            updateQuantity,
            getTotalItems,
            clearCartAfterSuccess,
            token
        }}>
            {children}
        </ClientPortalContext.Provider>
    );
};

export const useClientPortal = () => {
    const context = useContext(ClientPortalContext);
    if (!context) {
        throw new Error('useClientPortal must be used within a ClientPortalProvider');
    }
    return context;
};
